import { Nav } from "../components/Nav";
import { Logo } from "../components/Logo";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Scale } from "lucide-react";
import { PrintPdfButton } from "../components/PrintPdfButton";
import { VersionHistory } from "../components/VersionHistory";
import { LegalLanguageSwitcher } from "../components/LegalLanguageSwitcher";

const SECTIONS = [
    {
        n: 1,
        title: "Our Services",
        body: "SpLink.pro provides URL shortening, QR code generation, link management, analytics, and related services for individuals, businesses, and organizations. We may add, modify, or discontinue features at any time.",
    },
    {
        n: 2,
        title: "User Accounts",
        body: "Some features require an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
    },
    {
        n: 3,
        title: "Acceptable Use",
        body: "You agree not to use SpLink.pro to:",
        bullets: [
            "Violate any applicable law or regulation.",
            "Distribute malware, phishing links, spam, or malicious software.",
            "Promote illegal activities, fraud, scams, or deceptive practices.",
            "Infringe the intellectual property or privacy rights of others.",
            "Upload or link to harmful, abusive, or unlawful content.",
        ],
        after: "We reserve the right to suspend or remove any link or account that violates these Terms.",
    },
    {
        n: 4,
        title: "Link Availability",
        body: "While we strive to provide reliable service, we do not guarantee that shortened links or QR codes will always remain available or uninterrupted. We may modify, disable, or remove links when required for legal, security, or operational reasons.",
    },
    {
        n: 5,
        title: "Intellectual Property",
        body: "SpLink.pro™, its logos, branding, software, and content are owned by or licensed to SocialPulse and are protected by applicable intellectual property laws. You retain ownership of the content you link to.",
    },
    {
        n: 6,
        title: "Third-Party Content",
        body: "SpLink.pro may redirect users to third-party websites. We are not responsible for the content, products, services, or privacy practices of those websites.",
    },
    {
        n: 7,
        title: "Privacy",
        body: "Your use of SpLink.pro is also governed by our Privacy Policy and Cookie Policy, which explain how we collect, use, and protect your information.",
    },
    {
        n: 8,
        title: "Limitation of Liability",
        body: "To the fullest extent permitted by law, SpLink.pro and SocialPulse shall not be liable for any indirect, incidental, consequential, special, or punitive damages arising from your use of the service, including loss of data, revenue, or business opportunities.",
    },
    {
        n: 9,
        title: "Termination",
        body: "We may suspend or terminate your access to the service at any time if you violate these Terms or if necessary to protect the security, integrity, or operation of the platform.",
    },
    {
        n: 10,
        title: "Changes to These Terms",
        body: "We may update these Terms from time to time. Continued use of the service after changes become effective constitutes acceptance of the revised Terms.",
    },
    {
        n: 11,
        title: "Governing Law",
        body: "These Terms shall be governed by the laws of the jurisdiction in which SpLink.pro operates, unless otherwise required by applicable law.",
    },
];

export default function Terms() {
    return (
        <div className="App min-h-screen">
            <Nav />
            <LegalLanguageSwitcher doc="terms">

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden />
                <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[hsl(217,91%,60%)]/10 blur-3xl" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-6 pb-14 pt-24">
                    <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="terms-back-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium uppercase tracking-widest text-muted-foreground" data-testid="terms-badge">
                        <Scale className="h-3 w-3 text-[hsl(217,91%,45%)]" />
                        Legal
                    </div>
                    <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl" data-testid="terms-heading">
                        SpLink.pro™<br />
                        <span className="brand-gradient-text">Terms of Service</span>
                    </h1>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="font-mono text-xs text-muted-foreground">
                            <span className="text-foreground/60">Effective date:</span>{" "}
                            <span className="ml-1">[Insert Date]</span>
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                            <span className="text-foreground/60">Last updated:</span>{" "}
                            <span className="ml-1">[Insert Date]</span>
                        </div>
                        <PrintPdfButton testId="terms-print-btn" />
                    </div>
                    <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        Welcome to <strong className="text-foreground">SpLink.pro™</strong>, a URL shortening, QR code, and link management service that is part of the SocialPulse ecosystem. By accessing or using SpLink.pro, you agree to these Terms of Service. If you do not agree, please do not use our services.
                    </p>
                </div>
            </section>

            {/* Table of contents + content */}
            <section className="border-b border-border">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-16 lg:grid-cols-[240px_1fr]">
                    {/* TOC */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Contents</div>
                            <nav className="flex flex-col gap-1.5 font-mono text-xs">
                                {SECTIONS.map((s) => (
                                    <a
                                        key={s.n}
                                        href={`#section-${s.n}`}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid={`toc-link-${s.n}`}
                                    >
                                        <span className="text-[hsl(217,91%,45%)]">{String(s.n).padStart(2, "0")}</span>{" "}
                                        {s.title}
                                    </a>
                                ))}
                                <a href="#section-12" className="text-muted-foreground hover:text-foreground" data-testid="toc-link-12">
                                    <span className="text-[hsl(217,91%,45%)]">12</span> Contact Us
                                </a>
                            </nav>
                        </div>
                    </aside>

                    <article className="max-w-3xl">
                        {SECTIONS.map((s) => (
                            <section
                                key={s.n}
                                id={`section-${s.n}`}
                                className="border-b border-border py-8 first:pt-0"
                                data-testid={`terms-section-${s.n}`}
                            >
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Section {String(s.n).padStart(2, "0")}
                                </div>
                                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                                    {s.title}
                                </h2>
                                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                                {s.bullets && (
                                    <ul className="mt-4 space-y-2">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="flex gap-3 text-base text-muted-foreground">
                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(100,76%,42%)]" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {s.after && (
                                    <p className="mt-4 rounded-md border-l-4 border-[hsl(217,91%,45%)] bg-card p-4 text-base leading-relaxed text-foreground">
                                        {s.after}
                                    </p>
                                )}
                            </section>
                        ))}

                        {/* Contact */}
                        <section id="section-12" className="py-8" data-testid="terms-section-12">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 12</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                                Contact Us
                            </h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                If you have questions about these Terms, please contact us:
                            </p>
                            <div className="mt-6 border border-border bg-card p-6">
                                <div className="font-display text-lg font-bold">SpLink.pro™</div>
                                <div className="mt-1 text-sm text-muted-foreground">Part of the SocialPulse Ecosystem</div>
                                <div className="mt-4 space-y-2 font-mono text-sm">
                                    <a href="mailto:support@splink.pro" className="flex items-center gap-2 text-primary hover:underline" data-testid="terms-contact-email">
                                        <Mail className="h-4 w-4" /> support@splink.pro
                                    </a>
                                    <a href="https://splink.pro" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline" data-testid="terms-contact-web">
                                        splink.pro
                                    </a>
                                </div>
                            </div>

                            <p className="mt-10 border-t border-border pt-8 text-sm italic text-muted-foreground">
                                By using SpLink.pro™, you acknowledge that you have read, understood, and agree to these Terms of Service.
                            </p>
                        </section>

                        <VersionHistory docKey="terms" testId="terms-version-history" />
                    </article>
                </div>
            </section>

            </LegalLanguageSwitcher>

            <footer className="mx-auto max-w-6xl px-6 py-12">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <Logo />
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground">About</Link>
                        <Link to="/terms" className="hover:text-foreground">Terms</Link>
                        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                        <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
                        <Link to="/dpa" className="hover:text-foreground">DPA</Link>
                        <span>© {new Date().getFullYear()} SpLink.pro™</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
